import React, { useState, useEffect, useCallback } from "react";
import { useDispatch, useSelector } from "react-redux";
import CgFormCreator from "../../common/CgFormCreator";
import PopupModal from "../../common/PopupModal";
import "./userManagement.scss";
import {
  fetchUsers,
  createUser,
  updateUser as updateUserAPI,
  resetUserPassword,
} from "../../store/UserManagement/userManagementActions";
import {
  resetCreateUserStatus,
  resetUpdateUserStatus,
  resetPasswordStatus,
} from "../../store/UserManagement/userManagementReducer";
import rolesImage from "../../assets/images/rolesImage.svg";
import enableImage from "../../assets/images/enableImage.svg";
import { Link } from "react-router-dom";
import OutsideClickHandler from "../../common/OutsideClickHandler";

const ROLE_OPTIONS = {
  ADMIN: {
    displayId: "ADMIN_ROLE",
    name: "Role Administrator",
    code: "ROLE_ADMINISTRATOR",
  },
  MANAGER: {
    displayId: "MANAGER_ROLE",
    name: "Role Manager",
    code: "ROLE_MANAGER",
  },
  PRACTICE: {
    displayId: "PRACTICE_ROLE",
    name: "Role Practice",
    code: "ROLE_PRACTICE",
  },
};

const YES_NO_OPTIONS = [
  {
    code: "true",
    name: "Yes",
  },
  {
    code: "false",
    name: "No",
  },
];

const getRoleOptions = (includePractice = false) => {
  const baseOptions = [ROLE_OPTIONS.ADMIN, ROLE_OPTIONS.MANAGER];
  return includePractice
    ? [...baseOptions, ROLE_OPTIONS.PRACTICE]
    : baseOptions;
};

const UserManagement = () => {
  const dispatch = useDispatch();
  const [searchQuery, setSearchQuery] = useState("");
  const [selectedRoles, setSelectedRoles] = useState([]);
  const [statusFilter, setStatusFilter] = useState("Enabled");
  const [showResults, setShowResults] = useState(false);
  const [openPopup, setOpenPopup] = useState(false);
  const [editingUser, setEditingUser] = useState(null);
  const [activePopover, setActivePopover] = useState(null);
  const [currentPage, SetCurrentPage] = useState(1);
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const [allUsers, setAllUsers] = useState([]);

  const [validatedValues, setValidatedValues] = useState({
    username: "",
  });

  const { isCreatingUser, createUserSuccess } = useSelector(
    (state) => state.createUser
  );

  const { isUpdatingUser, updateUserSuccess } = useSelector(
    (state) => state.updateUser
  );

  const { isResettingPassword, resetPasswordError, resetPasswordSuccess } =
    useSelector((state) => state.resetPassword);

  const { users, usersError } = useSelector((state) => state.fetchUsers);

  useEffect(() => {
    if (createUserSuccess) {
      const timer = setTimeout(() => {
        dispatch(resetCreateUserStatus());
      }, 3000);
      return () => clearTimeout(timer);
    }
  }, [createUserSuccess, dispatch]);

  useEffect(() => {
    if (updateUserSuccess) {
      const timer = setTimeout(() => {
        dispatch(resetUpdateUserStatus());
      }, 3000);
      return () => clearTimeout(timer);
    }
  }, [updateUserSuccess, dispatch]);

  useEffect(() => {
    if (resetPasswordSuccess) {
      const timer = setTimeout(() => {
        dispatch(resetPasswordStatus());
      }, 3000);
      return () => clearTimeout(timer);
    }
  }, [resetPasswordSuccess, dispatch]);

  const getStatusParam = () => {
    if (statusFilter === "Disabled") return "false";
    else if (statusFilter === "Show All") return "all";
    else return "true";
  };

  const fetchFilteredUsers = (search, roles, status) => {
    if (search && search.trim().length > 0) {
      dispatch(
        fetchUsers({
          searchString: search.trim(),
          roles: roles,
          status: status,
          pageNumber: 1,
          pageSize: 10,
        })
      )
        .unwrap()
        .then((response) => {
          setShowResults(true);
          const page1Users = response.data.data.userPagingDTO.users;
          setAllUsers(page1Users);
          SetCurrentPage(1);
        })
        .catch((error) => {
          dispatch({
            type: "fetchUsers/fulfilled",
            payload: {
              metadata: {
                outcome: {
                  status: error.status || 404,
                  message: error.message || "Data not found.",
                },
              },
              data: {
                userPagingDTO: {
                  users: [],
                  totalUsers: 0,
                  totalActiveUsers: 0,
                },
              },
            },
          });
          setShowResults(false);
          setAllUsers([]);
          SetCurrentPage(1);
        });
    } else {
      dispatch({
        type: "fetchUsers/fulfilled",
        payload: {
          metadata: { outcome: { status: 200 } },
          data: {
            userPagingDTO: { users: [], totalUsers: 0, totalActiveUsers: 0 },
          },
        },
      });
      setShowResults(false);
      setAllUsers([]);
      SetCurrentPage(1);
    }
  };

  const handleRoleSelect = (name, value) => {
    if (!value) return;

    const selectedRole = Object.values(ROLE_OPTIONS).find(
      (role) => role.name === value
    )?.code;

    if (selectedRole) {
      setSelectedRoles((prevRoles) => {
        const newRoles = prevRoles.includes(selectedRole)
          ? prevRoles.filter((role) => role !== selectedRole)
          : [...prevRoles, selectedRole];

        if (searchQuery && searchQuery.length > 0) {
          fetchFilteredUsers(searchQuery, newRoles, getStatusParam());
        }
        return newRoles;
      });
    }
  };

  const handleStatusFilterChange = (value) => {
    setStatusFilter(value);

    let statusParam = "true";
    if (value === "Disabled") statusParam = "false";
    else if (value === "Show All") statusParam = "all";

    if (searchQuery && searchQuery.length > 0) {
      fetchFilteredUsers(searchQuery, selectedRoles, statusParam);
    }
  };

  const handleSearchQueryChange = (value) => {
    setSearchQuery(value);

    if (value && value.length > 0) {
      fetchFilteredUsers(value, selectedRoles, getStatusParam());
    } else {
      setShowResults(false);
      dispatch({
        type: "fetchUsers/fulfilled",
        payload: {
          metadata: { outcome: { status: 200 } },
          data: {
            userPagingDTO: { users: [], totalUsers: 0, totalActiveUsers: 0 },
          },
        },
      });
      dispatch({
        type: "fetchUsers/pending",
      });
    }
  };

  const setupInfiniteScrolling = (callback) => {
    const handleScroll = () => {
      const windowHeight =
        window.innerHeight || document.documentElement.clientHeight;
      const body = document.body;
      const html = document.documentElement;
      const docHeight = Math.max(
        body.scrollHeight,
        body.offsetHeight,
        html.clientHeight,
        html.scrollHeight,
        html.offsetHeight
      );
      const windowBottom = windowHeight + window.pageYOffset;

      if (windowBottom >= docHeight - 100) {
        callback();
      }
    };

    window.addEventListener("scroll", handleScroll);

    return () => {
      window.removeEventListener("scroll", handleScroll);
    };
  };

  const hasApiError = usersError || users?.metadata?.outcome?.status === 404;
  const displayUsers = !hasApiError && showResults ? allUsers : [];
  const totalUsers = !hasApiError && showResults ? users?.totalUsers || 0 : 0;

  const morePagesExist = (totalItems) => {
    const pageSize = 10;
    if (pageSize > totalItems) return false;
    const possiblePages = Math.ceil(totalItems / pageSize);
    return possiblePages > currentPage;
  };

  const infiniteScroll = useCallback(() => {
    if (isLoadingMore || !searchQuery || !showResults) return;

    if (morePagesExist(totalUsers)) {
      setIsLoadingMore(true);
      const nextPage = currentPage + 1;
      dispatch(
        fetchUsers({
          searchString: searchQuery.trim(),
          roles: selectedRoles,
          status: getStatusParam(),
          pageNumber: nextPage,
          pageSize: 10,
        })
      )
        .unwrap()
        .then((response) => {
          let newUsers = [];
          newUsers = response.data.data.userPagingDTO.users;
          
          setAllUsers((prev) => {
            const combined = [...prev, ...newUsers];
            const deduped = combined.filter((user, idx, arr) =>
              arr.findIndex(u => u.displayId === user.displayId) === idx
            );
            return deduped;
          });

          SetCurrentPage(nextPage);
        })
        .catch((error) => {
          console.error("Error loading more users:", error);
        })
        .finally(() => {
          setIsLoadingMore(false);
        });
    }
  }, [
    isLoadingMore,
    searchQuery,
    showResults,
    totalUsers,
    currentPage,
    selectedRoles,
    statusFilter,
    dispatch,
  ]);

  useEffect(() => {
    const cleanup = setupInfiniteScrolling(infiniteScroll);
    return cleanup;
  }, [infiniteScroll]);

  useEffect(() => {
    SetCurrentPage(1);
    setAllUsers([]);
  }, [searchQuery, selectedRoles, statusFilter]);

  useEffect(() => {
    if (showResults && users?.users && currentPage === 1) {
      
      setAllUsers(prevUsers => {
        if (prevUsers.length === 0) {
          return users.users;
        }
        return prevUsers;
      });
    }
  }, [users?.users, showResults]);

  const handlePasswordResetEmail = (username) => {
    dispatch(resetUserPassword(username))
      .unwrap()
      .then((response) => {
        return response;
      })
      .catch((error) => {
        return error;
      });
  };

  const searchSchema = [
    {
      component: "input",
      type: "text",
      name: "searchQuery",
      label: "USER SEARCH",
      placeholder: "Enter Name, Last Name, or email",
      className: "col-md-6 search-input",
      onKeyChangeCallback: (name, value) => {
        handleSearchQueryChange(value);
      },
      valueChangeCallback: (name, value) => {
        handleSearchQueryChange(value);

        const searchInput = document.querySelector('input[name="searchQuery"]');
        if (searchInput) {
          searchInput.value = value;
        }
      },
    },
    {
      component: "dropdown",
      name: "roleFilter",
      label: "FILTER BY ROLE",
      className: "col-md-3 role-filter",
      dropdownPlaceHolder: "Select one...",
      alwaysShowPlaceholder: true,
      dropdownOptions: [
        ...Object.values(ROLE_OPTIONS).map((role) => ({
          value: role.name,
          label: role.name,
        })),
      ],
      onSelectCallBack: (name, value) => {
        handleRoleSelect(name, value);
      },
      valueChangeCallback: () => {},
    },
    {
      component: "dropdown",
      name: "statusFilter",
      label: "FILTER BY STATUS",
      className: "col-md-3 status-filter",
      dropdownOptions: [
        { value: "Show All", label: "Show All" },
        { value: "Enabled", label: "Enabled" },
        { value: "Disabled", label: "Disabled" },
      ],
      defaultValue: "Enabled",
      onSelectCallBack: (name, value) => {
        handleStatusFilterChange(value);
      },
      valueChangeCallback: () => {},
    },
  ];

  const addUserSchema = [
    {
      component: "input",
      type: "text",
      name: "firstName",
      label: "FIRST NAME",
      placeholder: "Enter first name",
      required: true,
      className: "addUserFields",
      valueChangeCallback: () => {},
    },
    {
      component: "input",
      type: "text",
      name: "lastName",
      label: "LAST NAME",
      placeholder: "Enter last name",
      required: true,
      className: "addUserFields",
      valueChangeCallback: () => {},
    },
    {
      component: "input",
      type: "email",
      name: "username",
      label: "USERNAME/EMAIL",
      placeholder: "Enter email address",
      required: true,
      className:
        validatedValues?.username === 0 ? "errorBorder" : "addUserFields",
      onKeyChangeCallback: (name, value) => {
        setValidatedValues({ ...validatedValues, [name]: value });
      },
      valueChangeCallback: () => {},
    },

    {
      component: "checkbox",
      name: "role",
      label: "ROLE",
      className: "addUserFields",
      shouldRenderCheckboxWithBackgroundImageStyle: true,
      options: getRoleOptions(),
      keyValues: { label: "name", value: "code" },
      defaultValues: [],
      required: true,
      onSelect: (selected) => selected,
      valueChangeCallback: () => {},
      customLabel: true,
      customRenderLabel: () => (
        <div className="checkbox-label">
          <span>ROLES</span>
        </div>
      ),
    },
  ];

  const editUserSchema = [
    {
      component: "input",
      type: "text",
      name: "firstName",
      label: "First Name",
      placeholder: "Enter first name",
      required: true,
      className: "editUserFields firstname",
      defaultValue: editingUser?.firstName || "",
      valueChangeCallback: () => {},
    },
    {
      component: "input",
      type: "text",
      name: "lastName",
      label: "Last Name",
      placeholder: "Enter last name",
      required: true,
      className: "editUserFields",
      defaultValue: editingUser?.lastName || "",
      valueChangeCallback: () => {},
    },
    {
      component: "input",
      type: "text",
      name: "username",
      label: "Username/Email",
      placeholder: "Enter email address",
      required: true,
      className: "editUserFields",
      defaultValue: editingUser?.username || "",
      valueChangeCallback: () => {},
    },
    {
      component: "radio",
      name: "enabled",
      label: "Active",
      className: "editUserFields",
      options: YES_NO_OPTIONS,
      keyValues: { label: "name", value: "code" },
      defaultValue: editingUser?.enabled || "false",
      required: true,
      onSelect: (selected) => {
        const booleanValue = selected === "true" ? "true" : "false";
        setEditingUser((prev) => ({
          ...prev,
          enabled: booleanValue,
        }));
      },
      valueChangeCallback: (name, value) => {
        const booleanValue = value === "true" ? "true" : "false";
        setEditingUser((prev) => ({
          ...prev,
          [name]: booleanValue,
        }));
      },
      customLabel: true,
      customRenderLabel: () => (
        <div className="radio-label">
          <span>Active</span>
        </div>
      ),
    },

    {
      component: "radio",
      name: "assignAppointments",
      label: "Assign Appointments",
      className: "editUserFields",
      options: YES_NO_OPTIONS,
      keyValues: { label: "name", value: "code" },
      defaultValue:
        editingUser?.assignAppointments === true ||
        editingUser?.assignAppointments === "true"
          ? "true"
          : "false",
      required: true,
      onSelect: (selected) => {
        const booleanValue = selected === "true" ? "true" : "false";
        setEditingUser((prev) => ({
          ...prev,
          assignAppointments: booleanValue,
        }));
      },
      valueChangeCallback: (name, value) => {
        const booleanValue = value === "true" ? "true" : "false";
        setEditingUser((prev) => ({
          ...prev,
          [name]: booleanValue,
        }));
      },
      customLabel: true,
      customRenderLabel: () => (
        <div className="radio-label">
          <span>ASSIGN APPOINTMENTS</span>
        </div>
      ),
    },
    {
      component: "checkbox",
      name: "role",
      label: "Role",
      className: "editUserFields",
      shouldRenderCheckboxWithBackgroundImageStyle: true,
      options: getRoleOptions(true),
      keyValues: { label: "name", value: "code" },
      defaultValues: editingUser?.grantedRoles
        ? Array.isArray(editingUser.grantedRoles)
          ? editingUser.grantedRoles.map((role) => {
              if (role === "ROLE_ADMIN") return "ROLE_ADMINISTRATOR";
              return role;
            })
          : editingUser.grantedRoles.split(",").map((role) => {
              const trimmedRole = role.trim();
              if (trimmedRole === "ROLE_ADMIN") return "ROLE_ADMINISTRATOR";
              return trimmedRole;
            })
        : [],
      required: true,
      onSelect: (selected) => selected,
      valueChangeCallback: () => {},
      customLabel: true,
      customRenderLabel: () => (
        <div className="checkbox-label">
          <span>ROLES</span>
        </div>
      ),
    },
  ];
  const SelectedRolesTags = ({ selectedRoles, onRemoveRole }) => {
    const getRoleName = (roleCode) => {
      const role = Object.values(ROLE_OPTIONS).find((r) => r.code === roleCode);
      return role ? role.name : roleCode;
    };

    if (selectedRoles.length === 0) return null;

    return (
      <div className="selected-roles-container">
        <div className="selected-roles-tags">
          {selectedRoles.map((role) => (
            <span key={role} className="role-tag">
              {getRoleName(role)}
              <a onClick={() => onRemoveRole(role)} className="remove-tag">
                ×
              </a>
            </span>
          ))}
        </div>
      </div>
    );
  };

  const RenderCreateUserForm = ({ onClose }) => {
    return (
      <div className="create-user-form">
        {editingUser && (
          <div className="password-reset-section">
            <button
              className="btn btn-primary med-pill sendPassword"
              onClick={() => handlePasswordResetEmail(editingUser.username)}
              disabled={isResettingPassword}
            >
              {isResettingPassword ? "Sending..." : "Send Password Reset Email"}
            </button>

            {resetPasswordError && (
              <div className="error-message alert alert-danger mt-2">
                Failed to send password reset email: {resetPasswordError}
              </div>
            )}
          </div>
        )}

        <div className="userManagementForm-without-border-style userManagementForm">
          <CgFormCreator
            schema={editingUser ? editUserSchema : addUserSchema}
            onSubmitCallback={(formData) => {
              const userData = {
                ...editingUser,
                ...formData,
                displayId:
                  editingUser?.displayId ||
                  Math.random().toString(36).substr(2, 9),
                grantedRoles: Array.isArray(formData.role)
                  ? formData.role
                  : formData.role
                  ? [formData.role]
                  : [],
              };

              if (editingUser) {
                const updateData = {
                  ...userData,
                  originalUsername: editingUser.username,
                };

                dispatch(updateUserAPI(updateData))
                  .unwrap()
                  .then(() => {
                    if (searchQuery && searchQuery.length > 0) {
                      SetCurrentPage(1);
                      setAllUsers([]);
                      fetchFilteredUsers(
                        searchQuery,
                        selectedRoles,
                        getStatusParam()
                      );
                    }
                    onClose();
                  })
                  .catch((error) => {
                    return error;
                  });
              } else {
                const createUserData = {
                  firstName: formData.firstName,
                  lastName: formData.lastName,
                  username: formData.username,
                  enabled:
                    formData.enabled === "true" || formData.enabled === true,
                  confirmed: false,
                  assignAppointments: false,
                  // assignAppointments: formData.assignAppointments === "true" || formData.assignAppointments === true,
                  grantedRoles: formData.role
                    ? Array.isArray(formData.role)
                      ? formData.role.map((role) =>
                          role === "ROLE_ADMINISTRATOR" ? "ROLE_ADMIN" : role
                        )
                      : [
                          formData.role === "ROLE_ADMINISTRATOR"
                            ? "ROLE_ADMIN"
                            : formData.role,
                        ]
                    : [],
                };
                console.log(createUserData, formData.role);

                dispatch(createUser(createUserData))
                  .unwrap()
                  .then(() => {
                    onClose();
                    setSearchQuery("");
                    setShowResults(false);

                    const searchInput = document.querySelector(
                      'input[name="searchQuery"]'
                    );
                    if (searchInput) {
                      searchInput.value = "";
                    }

                    dispatch({
                      type: "fetchUsers/fulfilled",
                      payload: {
                        metadata: { outcome: { status: 200 } },
                        data: {
                          userPagingDTO: {
                            users: [],
                            totalUsers: 0,
                            totalActiveUsers: 0,
                          },
                        },
                      },
                    });
                  })
                  .catch((error) => {
                    return error;
                  });
              }
            }}
            primaryButton={editingUser ? "Update" : "Create"}
            secondaryButton="Cancel"
            onCancel={onClose}
            isLoading={editingUser ? isUpdatingUser : isCreatingUser}
          />
        </div>
      </div>
    );
  };

  const RenderSearchSection = () => {
    const handleRemoveRole = (roleToRemove) => {
      setSelectedRoles((prevRoles) => {
        const newRoles = prevRoles.filter((role) => role !== roleToRemove);
        if (searchQuery && searchQuery.length > 0) {
          fetchFilteredUsers(searchQuery, newRoles, getStatusParam());
        }
        return newRoles;
      });
    };

    const handleClearRoles = () => {
      setSelectedRoles([]);
      if (searchQuery && searchQuery.length > 0) {
        fetchFilteredUsers(searchQuery, [], getStatusParam());
      }
    };

    return (
      <>
        <div className="header-section">
          <h2 className="section-header">User Management</h2>
          <button
            className="btn btn-primary med-pill pull-right ng-scope"
            onClick={() => {
              setEditingUser(null);
              setOpenPopup(true);
            }}
          >
            Create User
          </button>
        </div>
        <div className="search-section">
          <CgFormCreator
            schema={searchSchema}
            noSubmit={true}
            needFormHeader={false}
            formClassName="search-form"
          />
          {selectedRoles.length > 0 && (
            <a className="clear-roles-btn" onClick={handleClearRoles}>
              Clear
            </a>
          )}
          <SelectedRolesTags
            selectedRoles={selectedRoles}
            onRemoveRole={handleRemoveRole}
          />
        </div>
      </>
    );
  };

  const RenderUserCount = () => {
    if (!showResults || !searchQuery || hasApiError || totalUsers === 0) {
      return null;
    }

    return (
      <div className="user-count-section">
        <span>{allUsers?.length} Users</span>
      </div>
    );
  };

  const RenderTable = () => {
    const formatRoleDisplay = (roles) => {
      if (!roles) return "";
      const rolesArray = Array.isArray(roles) ? roles : roles.split(",");
      return rolesArray
        .map((role) => {
          const trimmedRole = typeof role === "string" ? role.trim() : role;
          if (trimmedRole === "ROLE_ADMINISTRATOR") {
            return "ROLE_ADMINISTRATOR";
          } else if (trimmedRole === "ROLE_MANAGER") {
            return "ROLE_MANAGER";
          } else if (trimmedRole === "ROLE_PRACTICE") {
            return "ROLE_PRACTICE";
          } else {
            return role;
          }
        })
        .join(", ");
    };

    return (
      <div className="table-section">
        <table className="user-table">
          <thead>
            <tr>
              <th>Enabled</th>
              <th>Username/Email</th>
              <th>First Name</th>
              <th>Last Name</th>
              <th>Roles</th>
              <th className="emptyHeading"></th>
            </tr>
          </thead>
          <tbody>
            {showResults &&
              !hasApiError &&
              displayUsers.map((user, index) => (
                <tr key={user.displayId}>
                  <td>
                    <img
                      className="enabledImg"
                      src={enableImage}
                      alt="enableImage"
                    />
                  </td>
                  <td>{user.username}</td>
                  <td>{user.firstName}</td>
                  <td>{user.lastName}</td>
                  <td>
                    {formatRoleDisplay(user.grantedRoles)}
                    <OutsideClickHandler
                      onClickOutside={() => {
                        if (activePopover === index) {
                          setActivePopover(null);
                        }
                      }}
                      isInLineFlex={true}
                    >
                      <span className="popup-relative-span">
                        {user?.practices ? (
                          <img
                            className="grantedRolesImg"
                            src={rolesImage}
                            alt="rolesImage"
                            onClick={() =>
                              setActivePopover(
                                activePopover === index ? null : index
                              )
                            }
                          />
                        ) : null}
                        {activePopover === index && user?.practices && (
                          <span className="practice-popover-contect">
                            <div className="arrow"></div>
                            {Array.isArray(user.practices) ? (
                              <ul className="more-list">
                                {user.practices.map((item, idx) => (
                                  <li key={idx}>
                                    <Link
                                      to={`/practice/${item?.kokoPracticeRef}/profile-information/info`}
                                    >
                                      {item.name}
                                    </Link>
                                    ({item?.kokoPracticeRef})
                                  </li>
                                ))}
                              </ul>
                            ) : (
                              <ul className="more-list">
                                <li>
                                  <Link
                                    to={`/practice/${user.practices.kokoPracticeRef}/profile-information/info`}
                                  >
                                    {user.practices.name}
                                  </Link>{" "}
                                  |{user.practices.kokoPracticeRef}
                                </li>
                              </ul>
                            )}
                          </span>
                        )}
                      </span>
                    </OutsideClickHandler>
                  </td>
                  <td>
                    <button
                      className="btn btn-link edit-button"
                      onClick={() => {
                        setOpenPopup(true);
                        setTimeout(() => {
                          const userToEdit = {
                            ...user,
                            enabled:
                              user.enabled === true || user.enabled === "true"
                                ? "true"
                                : "false",
                            assignAppointments:
                              user.assignAppointments === true ||
                              user.assignAppointments === "true"
                                ? "true"
                                : "false",
                            grantedRoles: Array.isArray(user.grantedRoles)
                              ? user.grantedRoles.map((role) =>
                                  role === "ROLE_ADMIN"
                                    ? "ROLE_ADMINISTRATOR"
                                    : role
                                )
                              : typeof user.grantedRoles === "string"
                              ? user.grantedRoles.split(",").map((role) => {
                                  const trimmedRole = role.trim();
                                  return trimmedRole === "ROLE_ADMIN"
                                    ? "ROLE_ADMINISTRATOR"
                                    : trimmedRole;
                                })
                              : [],
                          };
                          setEditingUser(userToEdit);
                        }, 0);
                      }}
                    >
                      Edit
                    </button>
                  </td>
                </tr>
              ))}
          </tbody>
        </table>
        {isLoadingMore && (
          <div className="loading-more-users">
            <p>Loading more users...</p>
          </div>
        )}
      </div>
    );
  };

  const RenderUserPopupModal = () => (
    <PopupModal
      isOpen={openPopup}
      onClose={() => {
        setOpenPopup(false);
        setEditingUser(null);
      }}
      popupHeading={editingUser ? "Edit User" : "Create New User"}
      customRender={() =>
        RenderCreateUserForm({
          onClose: () => {
            setOpenPopup(false);
            setEditingUser(null);
          },
        })
      }
    />
  );

  return (
    <div className="user-management-wrapper">
      {RenderSearchSection()}
      {RenderUserCount()}
      {RenderTable()}
      {openPopup && RenderUserPopupModal()}
    </div>
  );
};

export default UserManagement;
